/**
 * Browser softphone audio (ported from AI-Call-Center web/src/softphone.ts, 2026-10-31):
 * G.711 µlaw 8 kHz both ways. The microphone is resampled to 8 kHz, µlaw-encoded and sent in
 * 160-byte (20 ms) frames; the caller's µlaw is decoded, resampled and played through one
 * ScriptProcessor node. The browser's echo cancellation / noise suppression / AGC are on.
 */

const SEG_END = [0xff, 0x1ff, 0x3ff, 0x7ff, 0xfff, 0x1fff, 0x3fff, 0x7fff];

function linearToUlaw(sample) {
    const BIAS = 0x84;
    const CLIP = 32635;
    let sign = 0;
    if (sample < 0) {
        sample = -sample;
        sign = 0x80;
    }
    if (sample > CLIP) sample = CLIP;
    sample += BIAS;
    let exponent = 7;
    for (let i = 0; i < 8; i++) {
        if (sample <= SEG_END[i]) {
            exponent = i;
            break;
        }
    }
    const mantissa = (sample >> (exponent + 3)) & 0x0f;
    return ~(sign | (exponent << 4) | mantissa) & 0xff;
}

function ulawToLinear(u) {
    u = ~u & 0xff;
    const sign = u & 0x80;
    const exponent = (u >> 4) & 0x07;
    const mantissa = u & 0x0f;
    let t = ((mantissa << 3) + 0x84) << exponent;
    t -= 0x84;
    return sign ? -t : t;
}

/** Linear-interpolation resampler (fine for telephone speech). */
function resample(input, inRate, outRate) {
    if (inRate === outRate) return input;
    const ratio = inRate / outRate;
    const outLen = Math.max(0, Math.floor(input.length / ratio));
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
        const pos = i * ratio;
        const i0 = Math.floor(pos);
        const frac = pos - i0;
        const a = input[i0] ?? 0;
        const b = input[i0 + 1] ?? a;
        out[i] = a + (b - a) * frac;
    }
    return out;
}

const MIC_CONSTRAINTS = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };

/** Ask for the microphone before a call is placed or answered (a missing mic must not leave the customer in silence). */
export async function preflightMic() {
    const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    stream.getTracks().forEach((t) => t.stop());
}

/** Where the device's own microphone switch is (the browser can't show a prompt while the OS hides the mic). */
export function osMicSteps() {
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'Phone: Settings → Apps → Chrome → Permissions → Microphone → Allow.';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone: Settings → Chrome (or Safari) → Microphone → turn on.';
    if (/Windows/i.test(ua)) return 'Windows: Settings → Privacy & security → Microphone → turn on "Microphone access" and "Let desktop apps access your microphone".';
    if (/Mac OS X|Macintosh/i.test(ua)) return 'Mac: System Settings → Privacy & Security → Microphone → turn on Google Chrome, then restart Chrome.';
    return "Turn on microphone access for your browser in the device's privacy settings.";
}

/**
 * What the browser can tell without asking: is this page allowed to ask, has the site been blocked
 * before, and how many microphones the browser can see (0 = nothing to allow: no device, or the OS
 * hides it from the browser — then no prompt can appear).
 */
export async function micStatus() {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return { supported: false, permission: 'unknown', inputs: -1 };
    let permission = 'unknown';
    try {
        permission = (await navigator.permissions.query({ name: 'microphone' })).state; // granted | prompt | denied
    } catch (e) {
        /* Safari / Firefox: unknown */
    }
    let inputs = -1;
    try {
        inputs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput').length;
    } catch (e) {
        /* unknown */
    }
    return { supported: true, permission, inputs };
}

/** A plain sentence (with the exact place to fix it) for a microphone problem. */
export function micErrorText(e, status = null) {
    switch (e?.name || '') {
        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return `The browser can't see any microphone, so it can't ask for permission. Plug in a headset or microphone — or, if this device has one, turn on microphone access for the browser: ${osMicSteps()} Then press "Allow microphone" again.`;
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError':
            return status?.permission === 'denied'
                ? 'The microphone is blocked for this site. Click the lock icon left of the address → Microphone → Allow, then reload the page.'
                : `The microphone was not allowed. Press "Allow microphone" and choose Allow — if no question appears: ${osMicSteps()}`;
        case 'NotReadableError':
        case 'TrackStartError':
            return 'Your microphone is being used by another app (Zoom, Meet, WhatsApp…) — close it and try again.';
        default:
            return !window.isSecureContext || !navigator.mediaDevices?.getUserMedia ? 'This browser cannot use a microphone here — open the site in Chrome.' : 'The microphone could not be started.';
    }
}

/** Microphone + speaker for one call. `send(Uint8Array)` gets each 20 ms µlaw frame. */
export class CallAudio {
    constructor(send) {
        this.send = send;
        this.ctx = null;
        this.node = null;
        this.micStream = null;
        this.playBuf = new Float32Array(0);
        this.micFrame = [];
        this.muted = false;
        this.stopped = false;
    }

    async start() {
        this.micStream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
        if (this.stopped) {
            this.micStream.getTracks().forEach((t) => t.stop());
            return;
        }
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        await this.ctx.resume();
        if (this.stopped) {
            this.ctx.close();
            this.micStream.getTracks().forEach((t) => t.stop());
            return;
        }
        const src = this.ctx.createMediaStreamSource(this.micStream);
        this.node = this.ctx.createScriptProcessor(2048, 1, 1);
        this.node.onaudioprocess = (e) => this.process(e);
        src.connect(this.node);
        this.node.connect(this.ctx.destination);
    }

    setMuted(m) {
        this.muted = m;
    }

    /** The caller's voice (µlaw bytes from the server). */
    play(ulaw) {
        if (!this.ctx) return;
        const pcm8k = new Float32Array(ulaw.length);
        for (let i = 0; i < ulaw.length; i++) pcm8k[i] = ulawToLinear(ulaw[i]) / 32768;
        const up = resample(pcm8k, 8000, this.ctx.sampleRate);
        const merged = new Float32Array(this.playBuf.length + up.length);
        merged.set(this.playBuf);
        merged.set(up, this.playBuf.length);
        const cap = Math.floor(this.ctx.sampleRate * 0.5); // at most ~0.5 s waiting, to keep the delay low
        this.playBuf = merged.length > cap ? merged.slice(merged.length - cap) : merged;
    }

    process(e) {
        const out = e.outputBuffer.getChannelData(0);
        const n = Math.min(out.length, this.playBuf.length);
        for (let i = 0; i < n; i++) out[i] = this.playBuf[i];
        for (let i = n; i < out.length; i++) out[i] = 0;
        this.playBuf = this.playBuf.length > out.length ? this.playBuf.slice(out.length) : new Float32Array(0);

        if (this.muted || !this.ctx) return;
        const input = e.inputBuffer.getChannelData(0);
        const down = resample(input, this.ctx.sampleRate, 8000);
        for (let i = 0; i < down.length; i++) {
            const s = Math.max(-1, Math.min(1, down[i]));
            this.micFrame.push(linearToUlaw(s * 32767));
            if (this.micFrame.length >= 160) this.send(new Uint8Array(this.micFrame.splice(0, 160)));
        }
    }

    stop() {
        if (this.stopped) return;
        this.stopped = true;
        try {
            this.node && this.node.disconnect();
        } catch (e) {
            /* ignore */
        }
        try {
            this.micStream && this.micStream.getTracks().forEach((t) => t.stop());
        } catch (e) {
            /* ignore */
        }
        try {
            this.ctx && this.ctx.close();
        } catch (e) {
            /* ignore */
        }
        this.node = null;
        this.ctx = null;
    }
}

/** A soft two-tone ring while a call waits (Web Audio; silent until the page had a click). */
/**
 * Call tones (Web Audio, no files): "ringtone" for an incoming call, "ringback" while an
 * outgoing call is being set up / rings (440+480 Hz, 1 s on / 3 s off — like AI-Call-Center).
 */
export class Ringtone {
    constructor() {
        this.ctx = null;
        this.timer = null;
        this.mode = null;
    }

    start(mode = 'ringtone') {
        if (this.timer && this.mode === mode) return;
        this.stop();
        try {
            this.ctx = this.ctx || new (window.AudioContext || window.webkitAudioContext)();
            this.ctx.resume && this.ctx.resume();
        } catch (e) {
            return;
        }
        this.mode = mode;
        const tone = (freqs, offset, dur, peak) => {
            const ctx = this.ctx;
            if (!ctx) return;
            const gain = ctx.createGain();
            const t0 = ctx.currentTime + offset;
            gain.gain.setValueAtTime(0.0001, t0);
            gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.04);
            gain.gain.setValueAtTime(peak, t0 + dur - 0.06);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
            gain.connect(ctx.destination);
            freqs.forEach((f) => {
                const osc = ctx.createOscillator();
                osc.type = 'sine';
                osc.frequency.value = f;
                osc.connect(gain);
                osc.start(t0);
                osc.stop(t0 + dur + 0.02);
            });
        };
        if (mode === 'ringback') {
            const beat = () => tone([440, 480], 0.03, 1.0, 0.07);
            beat();
            this.timer = setInterval(beat, 4000);
            return;
        }
        const beep = () => {
            tone([440], 0, 0.4, 0.15);
            tone([480], 0.45, 0.4, 0.15);
        };
        beep();
        this.timer = setInterval(beep, 2500);
    }

    stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        this.mode = null;
    }
}
